import type { TopBarFeatureConfig } from '@milkdown/crepe/feature/top-bar'
import {
  JINJA_TOOLBAR_ITEMS,
  insertOrWrapJinjaToken,
  selectionJinjaForm,
} from './jinja/jinjaCommands'

// Derive the builder + item types from Crepe's public config type, so we never
// re-declare the GroupBuilder shape and stay correct across Crepe upgrades.
type BuildTopBar = NonNullable<TopBarFeatureConfig['buildTopBar']>
export type TopBarBuilder = Parameters<BuildTopBar>[0]
export type TopBarGroup = ReturnType<TopBarBuilder['addGroup']>
type TopBarItem = ReturnType<TopBarBuilder['build']>[number]['items'][number]

/**
 * Crepe's heading selector labels each row with one plain-text string used for
 * BOTH the toolbar button and the dropdown rows (and, before the editor is
 * "ready", the button shows `options[0].label`). To keep the button showing the
 * abbreviation ONLY — consistently, even pre-ready — the labels here are the
 * abbreviation (`H1`, `T`); the full row name (`Heading 1`, `Text`) is appended
 * by CSS `::after` in index.css, keyed by row order. We reuse Crepe's selector
 * (its `onSelect` sets the block, its `chevronIcon`) and only remap the labels.
 *
 * Keep these rows in sync with the `.top-bar-heading-option:nth-child(n)::after`
 * rules in index.css — they must match on count and order (row 1 = Paragraph/T).
 */
export const HEADING_ABBR: Record<string, string> = {
  Paragraph: 'T',
  'Heading 1': 'H1',
  'Heading 2': 'H2',
  'Heading 3': 'H3',
  'Heading 4': 'H4',
  'Heading 5': 'H5',
  'Heading 6': 'H6',
}

/** Re-add plucked default items (key + icon/active/onRun) into a fresh group. */
function addReused(
  group: TopBarGroup,
  items: (TopBarItem | undefined)[]
): void {
  for (const item of items) {
    if (!item) continue
    const { key, ...rest } = item
    group.addItem(key, rest)
  }
}

function withHeadingLabels(item: TopBarItem): TopBarItem {
  const selector = item.selector
  if (!selector) return item
  return {
    ...item,
    selector: {
      chevronIcon: selector.chevronIcon,
      activeLabel: (ctx) => HEADING_ABBR[selector.activeLabel(ctx)] ?? 'T',
      options: selector.options.map((option) => ({
        onSelect: option.onSelect,
        label: HEADING_ABBR[option.label] ?? 'T',
      })),
    },
  }
}

/**
 * The v3 formatting toolbar (Figma 1542:8681). Crepe's `buildTopBar` hook runs
 * AFTER the default groups are built and receives the populated builder, so we
 * pluck the default items we want — keeping Crepe's own icon / active-state /
 * command wiring verbatim — then `clear()` and re-add them in the design's
 * group structure, inserting a Jinja group where the design's (dropped)
 * alignment group sat.
 *
 *   Style:  heading · bold · italic
 *   List:   bullet · ordered
 *   Jinja:  {{ }} · {% %} · {# #} · { }      (insert / wrap selection)
 *   Insert: image · code block
 *
 * Intentionally omitted (no Crepe icon / non-portable markdown): strikethrough,
 * task-list, link, table, math, quote, hr, underline, text-alignment.
 *
 * Returns the Insert group so a caller can append feature-specific buttons —
 * see `addSnippetToolbarItem`. `builder.getGroup` only hands back a read-only
 * view, and keeping those buttons out of this signature stops it growing an
 * `onDoX` parameter per feature.
 */
export function buildFormattingTopBar(builder: TopBarBuilder): {
  insert: TopBarGroup
} {
  const pick = (groupKey: string, itemKey: string): TopBarItem | undefined =>
    builder.getGroup(groupKey)?.group.items.find((i) => i.key === itemKey)

  // Capture before clear() wipes the default groups.
  const heading = pick('heading', 'heading-selector')
  const wanted = {
    style: [
      heading ? withHeadingLabels(heading) : undefined,
      pick('formatting', 'bold'),
      pick('formatting', 'italic'),
    ],
    list: [pick('list', 'bullet-list'), pick('list', 'ordered-list')],
    insert: [pick('insert', 'image'), pick('block', 'code-block')],
  }

  builder.clear()

  addReused(builder.addGroup('style', 'Style'), wanted.style)
  addReused(builder.addGroup('list', 'List'), wanted.list)

  const jinja = builder.addGroup('jinja', 'Jinja')
  for (const item of JINJA_TOOLBAR_ITEMS) {
    jinja.addItem(item.key, {
      icon: item.icon,
      active: (ctx) => selectionJinjaForm(ctx) === item.open,
      onRun: (ctx) => insertOrWrapJinjaToken(ctx, item.open, item.close),
    })
  }

  const insert = builder.addGroup('insert', 'Insert')
  addReused(insert, wanted.insert)
  return { insert }
}
